/** @jsxImportSource @emotion/react */
import React from "react";
import { css } from "@emotion/react";
import { IClassBlock } from "src/hooks/styles";

export type IStyles = IClassBlock<
  [
    | "card"
    | "link"
    | "header"
    | "approvedLabel"
    | "imageOverlay"
    | "image"
    | "placeholder"
    | "favoriteButton"
    | "outOfBounds"
    | "body"
    | "priceLine"
    | "price"
    | "detailLine"
    | "detail"
    | "detailIcon"
    | "detailText"
    | "detailSeparator"
    | "addressLine"
    | "listDate"
    | "bodyGrid"
    | "bodyChip"
    | "bodyBadges"
    | "bodyRegulationContainer"
    | "bodyRegulation"
    | "bodyChipLabel"
    | "bodyChipValue"
    | "approvedLabelContainer"
    | "strRatingChipPositive"
    | "strRatingChipNeutral"
    | "strRatingChipNegative"
    | "strRatingChipUnknown",
  ]
>;

const styles: IStyles = {
  card: (theme) =>
    css({
      padding: theme.spaces.md,
      paddingTop: theme.spaces.xs,
      paddingBottom: theme.spaces.xs + theme.spaces.sm,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }),

  outOfBounds: (theme) =>
    css({
      position: "absolute",
      top: -8,
      left: -8,
      fontSize: theme.fontSizes.sm,
      background: theme.colorDanger,
      borderRadius: 4,
      color: theme.colorWhite,
      display: "inline-block",
      padding: `${theme.spaces.xxs}px ${theme.spaces.sm}px`,
      zIndex: 4,
    }),

  header: (theme) =>
    css({
      flex: "0 0 auto",
    }),

  link: (theme) =>
    css({
      textDecoration: "none",
      color: theme.colorTextPrimary,
    }),

  imageOverlay: (theme) =>
    css({
      marginBottom: theme.spaces.sm,
      padding: 0,
      marginLeft: -theme.spaces.xs,
      marginRight: -theme.spaces.xs,
      width: `calc(100% + ${theme.spaces.md}px)`,
      backgroundColor: theme.colorSecondary,
      overflow: "hidden",
      flex: "0 0 auto",
      "&::before": {
        position: "relative",
        zIndex: 2,
        backgroundImage: "radial-gradient(circle at 110% -10%, rgba(0, 0, 0, 0.35) 0px, transparent 32%)",
      },
    }),

  placeholder: () =>
    css({
      maxWidth: "80%",
      objectFit: "initial",
      width: "100%",
      height: "auto",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }),

  image: () =>
    css({
      objectFit: "cover",
      zIndex: 1,
    }),

  body: (theme) =>
    css({
      padding: 0,
      paddingTop: theme.spaces.sm,
      flex: "1 1 auto",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden", // prevent content overflow
    }),

  priceLine: (theme) =>
    css({
      display: "flex",
      flexWrap: "nowrap",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spaces.xs,
    }),

  price: (theme) =>
    css({
      fontSize: theme.fontSizes.lg,
      fontWeight: theme.fontWeight.bold,
      lineHeight: theme.lineHeight.xs,
    }),

  listDate: (theme) =>
    css({
      fontSize: theme.fontSizes.xs,
      color: theme.colorTextSecondary,
    }),

  detailLine: (theme) =>
    css({
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      marginBottom: theme.spaces.md,
      [theme.mq.xs]: {
        flexWrap: "wrap",
      },
    }),

  detail: (theme) =>
    css({
      display: "flex",
      flexWrap: "nowrap",
      alignItems: "center",
      fontSize: theme.fontSizes.xs,
    }),

  detailIcon: (theme) =>
    css({
      marginRight: theme.spaces.xxs,
    }),

  detailText: (theme) =>
    css({
      fontSize: theme.fontSizes.xs,
      textTransform: "capitalize",
      whiteSpace: "nowrap",
    }),

  detailSeparator: (theme) =>
    css({
      background: "#DFE2E4",
      display: "inline-block",
      width: 1,
      minWidth: 1,
      height: 14,
      margin: `0 ${theme.spaces.xs}px`,
    }),

  addressLine: (theme) =>
    css({
      fontSize: theme.fontSizes.xs,
      lineHeight: theme.lineHeight.xs,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      height: "1.5em",
      [theme.mq.xs]: {
        whiteSpace: "normal",
        height: "auto",
        maxHeight: "3em",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
      },
    }),

  bodyGrid: (theme) =>
    css({
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(1px, 1fr))", // expand on larger screens
      gap: theme.spaces.xs,
      [theme.mq.md]: {
        gridTemplateColumns: "repeat(auto-fit, minmax(40px, 1fr))", // at least 2 columns per row, min 40px on mobile
      },
    }),

  bodyChip: (theme) =>
    css({
      background: theme.colorSecondary,
      padding: theme.spaces.xs,
      borderRadius: 8,
      gridColumn: "span 1", // correct span syntax
      [theme.mq.xs]: {
        gridColumn: "span 3",
      },
      overflow: "hidden", // hide overflow text
      height: "fit-content",
    }),

  bodyBadges: (theme) =>
    css({
      display: "flex",
      flexDirection: "column",
      justifyContent: "start",
      alignItems: "flex-start",
      gridColumn: "span 2",
      [theme.mq.md]: {
        gridColumn: "span 4",
      },
    }),

  bodyChipLabel: (theme) =>
    css({
      color: theme.colorPrimary,
      fontSize: theme.fontSizes.xs,
      fontWeight: theme.fontWeight.normal,
      lineHeight: 1,
      display: "block",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      marginBottom: theme.spaces.xxs,
    }),

  bodyChipValue: (theme) =>
    css({
      fontSize: theme.fontSizes.xs,
      display: "block",
      fontWeight: theme.fontWeight.semiBold,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: "#c2410c", // matches !text-orange-700
    }),

  favoriteButton: (theme) =>
    css({
      position: "absolute",
      top: theme.spaces.xs,
      right: theme.spaces.xs,
      padding: theme.spaces.sm,
      zIndex: 3,
    }),

  approvedLabel: (theme) =>
    css({
      fontSize: theme.fontSizes.sm,
      padding: `${theme.spaces.xxs}px ${theme.spaces.sm}px`,
      borderRadius: theme.borderRadius,
      background: "rgba(255, 255, 255, 0.7)",
    }),

  approvedLabelContainer: (theme) =>
    css({
      position: "absolute",
      top: 12,
      left: 12,
      zIndex: 10,
      display: "flex",
      flexDirection: "column",
      gap: theme.spaces.xxs,
    }),

  bodyRegulation: (theme) =>
    css({
      width: 36,
      height: 36,
      borderRadius: "50%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gridColumn: "span 2",
      [theme.mq.md]: {
        gridColumn: "span 1",
      },
    }),

  bodyRegulationContainer: (theme) =>
    css({
      display: "flex",
      justifyContent: "end",
      alignItems: "center",
      gridColumn: "span 2",
      [theme.mq.md]: {
        gridColumn: "span 1",
      },
    }),

  strRatingChipPositive: (theme) =>
    css({
      background: "rgba(52, 168, 83, 0.08)",
      "& > span:first-of-type": {
        color: "#374151", // Professional dark gray for label
      },
      "& > span:last-of-type": {
        color: "#059669", // Sophisticated green for value
      },
    }),

  strRatingChipNeutral: (theme) =>
    css({
      background: "rgba(245, 158, 11, 0.08)",
      "& > span:first-of-type": {
        color: "#374151", // Professional dark gray for label
      },
      "& > span:last-of-type": {
        color: "#d97706", // Sophisticated amber for value
      },
    }),

  strRatingChipNegative: (theme) =>
    css({
      background: "rgba(239, 68, 68, 0.08)",
      "& > span:first-of-type": {
        color: "#374151", // Professional dark gray for label
      },
      "& > span:last-of-type": {
        color: "#dc2626", // Sophisticated red for value
      },
    }),

  strRatingChipUnknown: (theme) =>
    css({
      background: "rgba(107, 114, 128, 0.08)",
      "& > span:first-of-type": {
        color: "#374151", // Professional dark gray for label
      },
      "& > span:last-of-type": {
        color: "#6b7280", // Sophisticated gray for value
      },
    }),
};

export default styles;
